import { UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { showError, showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';

import { useInvalidateFavorites } from 'api/favorites/hooks';
import { handleError } from 'api/query-client';
import { isInTemplateRoute } from 'ui/router';

import {
  createDocumentTemplate,
  CreateUpdateDocumentTemplate,
  deleteDocumentTemplate,
  DocumentTemplate,
  documentTemplatePreview,
  editDocumentTemplate,
  loadConvertedDocument,
  loadDocument,
  loadDocumentTemplate,
  loadDocumentTemplateJourneys,
  loadDocumentTemplates,
  loadTemplateDocumentTemplates,
  moveDocumentTemplate,
} from './requests';

const useInvalidateDocumentTemplate = () => {
  const queryClient = useQueryClient();
  const invalidateDocumentTemplates = () => {
    queryClient.invalidateQueries(['document-templates']);
  };
  const invalidateDocumentTemplate = (shortId: DocumentTemplate['shortId']) => {
    queryClient.invalidateQueries(['document-template', shortId]);
  };
  return {
    invalidateDocumentTemplates,
    invalidateDocumentTemplate,
  };
};

export const useDocumentTemplates = (queryString?: string, disabled?: boolean) => {
  const loadFn = isInTemplateRoute() ? loadTemplateDocumentTemplates : loadDocumentTemplates;
  return useQuery({
    enabled: !!queryString && !disabled,
    queryKey: ['document-templates', queryString ?? ''],
    queryFn: () => loadFn(queryString),
  });
};

export const useDocumentTemplate = (shortId: string) => {
  return useQuery({
    queryKey: ['document-template', shortId],
    queryFn: () => loadDocumentTemplate(shortId),
  });
};

export const useDocumentTemplateJourneys = (shortId: string) => {
  return useQuery({
    enabled: Boolean(shortId),
    queryKey: ['document-template-journeys', shortId],
    queryFn: () => loadDocumentTemplateJourneys(shortId),
  });
};

export const useMoveDocumentTemplate = (shortId: string) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateDocumentTemplates } = useInvalidateDocumentTemplate();
  return useMutation({
    mutationFn: (workspaceId: string) => moveDocumentTemplate(shortId, workspaceId),
    onSuccess: () => {
      showSuccess(t('documentTemplateMoved'));
      invalidateDocumentTemplates();
    },
    onError: () => {
      handleError(t('documentTemplateNotMoved'));
    },
  });
};

export type DocumentErrorResponse = AxiosError<{
  error?: string;
}>;

export const useCreateDocumentTemplate = (
  options?: UseMutationOptions<any, DocumentErrorResponse, CreateUpdateDocumentTemplate>,
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateDocumentTemplates } = useInvalidateDocumentTemplate();
  return useMutation({
    ...options,
    mutationFn: (data) => createDocumentTemplate(data),
    onSuccess: (...data) => {
      invalidateDocumentTemplates();
      options?.onSuccess?.(...data);
    },
    onError: (error, variables, context) => {
      handleError(() => {
        if (error.response?.data?.error) {
          options?.onError?.(error, variables, context);
        } else {
          showError(t('documentNotCreated'));
        }
      });
    },
  });
};

export const useUpdateDocumentTemplateStatus = () => {
  const { invalidateDocumentTemplate, invalidateDocumentTemplates } =
    useInvalidateDocumentTemplate();
  return useMutation({
    mutationFn: ({ shortId, status }: Pick<DocumentTemplate, 'shortId' | 'status'>) => {
      return editDocumentTemplate(shortId, { status });
    },
    onSuccess: (requestData) => {
      invalidateDocumentTemplates();
      invalidateDocumentTemplate(requestData.shortId);
    },
  });
};

export const useEditDocumentFields = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shortId, fieldConfig }: Pick<DocumentTemplate, 'shortId' | 'fieldConfig'>) => {
      queryClient.setQueryData(
        ['document-template', shortId],
        (oldData: DocumentTemplate | undefined) => {
          if (oldData) {
            return { ...oldData, fieldConfig };
          }
          return oldData;
        },
      );
      return editDocumentTemplate(shortId, { fieldConfig });
    },
  });
};

export const useEditDocumentTemplate = (
  options?: UseMutationOptions<
    any,
    any,
    CreateUpdateDocumentTemplate & { id: string; exampleData?: { [key: string]: string } }
  >,
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateDocumentTemplate, invalidateDocumentTemplates } =
    useInvalidateDocumentTemplate();
  const { invalidateFavorites } = useInvalidateFavorites();
  return useMutation({
    ...options,
    mutationFn: ({ id, ...data }) => editDocumentTemplate(id, data),
    onSuccess: (data, variables, context) => {
      invalidateDocumentTemplates();
      invalidateDocumentTemplate(variables.id);
      invalidateFavorites();
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      handleError(() => {
        if (error.response?.data?.error) {
          options?.onError?.(error, variables, context);
        } else {
          showError(t('documentNotUpdated'));
        }
      });
    },
  });
};

export const useDocumentTemplatePreview = (shortId: string) => {
  const { invalidateDocumentTemplate } = useInvalidateDocumentTemplate();
  const showFilePreviewModal = usePreviewFileModal();
  return useMutation({
    mutationFn: (data: Record<string, string>) => documentTemplatePreview(shortId, data),
    onSuccess: (data) => {
      showFilePreviewModal({
        formFile: {
          src: data.url,
          name: data.name,
        },
        filesList: [{ src: data.url, name: data.name }],
      });
      invalidateDocumentTemplate(shortId);
    },
  });
};

export const useDeleteDocumentTemplate = (options?: UseMutationOptions<any, any, string>) => {
  const { invalidateFavorites } = useInvalidateFavorites();
  const { invalidateDocumentTemplates } = useInvalidateDocumentTemplate();

  return useMutation({
    ...options,
    mutationFn: (id) => deleteDocumentTemplate(id),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      invalidateDocumentTemplates();
      invalidateFavorites();
    },
  });
};

export const useDocument = (shortId: string) => {
  return useQuery({
    queryKey: ['document', shortId],
    queryFn: () => loadDocument(shortId),
  });
};

export const useConvertedDocument = (shortId: string) => {
  return useQuery({
    queryKey: ['document-converted', shortId],
    queryFn: () => loadConvertedDocument(shortId),
  });
};

const locale = {
  en: {
    documentNotCreated: 'Failed to create document',
    documentNotUpdated: 'Failed to update document',
    documentTemplateMoved: 'Document moved',
    documentTemplateNotMoved: 'Unable to move document',
  },
};
